
import { SavedChartState, UserProfile, ChartSetList } from '../types';

// FIX: Add global declarations for gapi and google to resolve TypeScript errors for the externally loaded Google API scripts.
declare var gapi: any;
declare var google: any;

// Google Drive API credentials loaded from environment variables
const API_KEY = import.meta.env.VITE_GOOGLE_DRIVE_API_KEY;
const CLIENT_ID = import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID; 

const SCOPES = 'https://www.googleapis.com/auth/drive.file';
const APP_FOLDER_NAME = 'Chord Notator';
const TOKEN_STORAGE_KEY = 'driveOAuthToken';

let gapiInited = false;
let appFolderId: string | null = null;

// Token storage interface
interface StoredToken {
    access_token: string;
    token_type: string;
    expires_at: number; // Unix timestamp in ms
}

// Save OAuth token to localStorage with expiration
const saveTokenToStorage = (accessToken: string, expiresIn: number): void => {
    const storedToken: StoredToken = {
        access_token: accessToken,
        token_type: 'Bearer',
        expires_at: Date.now() + (expiresIn * 1000) - 60000, // Subtract 1 minute buffer
    };
    localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(storedToken));
    console.log('💾 Drive OAuth token saved to storage');
};

// Get stored token if still valid
const getStoredToken = (): StoredToken | null => {
    try {
        const data = localStorage.getItem(TOKEN_STORAGE_KEY);
        if (!data) return null;
        
        const token = JSON.parse(data) as StoredToken;
        
        // Check if token is expired
        if (Date.now() >= token.expires_at) {
            console.log('⏰ Stored Drive token has expired');
            localStorage.removeItem(TOKEN_STORAGE_KEY);
            return null;
        }
        
        return token;
    } catch (error) {
        console.error('❌ Failed to parse stored token:', error);
        localStorage.removeItem(TOKEN_STORAGE_KEY);
        return null;
    }
};

// Clear stored token
const clearStoredToken = (): void => {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
};

// Helper function to handle popup OAuth token
const handlePopupToken = async (accessToken: string, urlParams: URLSearchParams): Promise<UserProfile | null> => {
    try {
        // Wait for GAPI to be ready if needed
        if (!gapiInited || !gapi.client) {
            await waitForGapi();
        }

        const expiresIn = parseInt(urlParams.get('expires_in') || '3600');

        // Set the token in the Google API client
        gapi.client.setToken({
            access_token: accessToken,
            token_type: 'Bearer',
            expires_in: expiresIn,
        });

        // Save token to localStorage for auto-reconnect
        saveTokenToStorage(accessToken, expiresIn);

        // Get user profile from Drive API
        const response = await gapi.client.drive.about.get({ fields: 'user' });
        const user = response.result.user;
        
        const userProfile = {
            name: user.displayName || "User",
            email: user.emailAddress || "user@example.com",
            imageUrl: user.photoLink || "",
        };

        // Store user profile in localStorage for persistence
        localStorage.setItem('driveUserProfile', JSON.stringify(userProfile));

        return userProfile;
    } catch (error) {
        console.error("Error processing popup token:", error);
        throw error;
    }
};

// Helper function to wait for GAPI to be ready
const waitForGapi = (): Promise<void> => {
    return new Promise((resolve) => {
        const checkGapi = () => {
            if (gapiInited && gapi && gapi.client) {
                resolve();
            } else {
                setTimeout(checkGapi, 100);
            }
        };
        checkGapi();
    });
};

export const initGoogleClient = (callback: () => void) => {
    const initializeGapiClient = () => {
        gapi.load('client', async () => {
            await gapi.client.init({
                apiKey: API_KEY,
                discoveryDocs: [
                    'https://www.googleapis.com/discovery/v1/apis/drive/v3/rest'
                ],
            });
            gapiInited = true;
            callback();
        });
    };

    // Just initialize the GAPI client since we're using popup OAuth
    initializeGapiClient();
};

export const signIn = (): Promise<UserProfile | null> => {
    return new Promise((resolve, reject) => {
        if (!gapiInited) {
            console.error("GAPI not ready for sign in.");
            return reject(new Error("GAPI not ready."));
        }
        
        try {
            // Clear any existing tokens first
            gapi.client.setToken(null);
            
            // Use popup for OAuth instead of redirect
            // Use origin as redirect_uri (must match Google Console authorized URIs)
            const redirectUri = window.location.origin;
            const scope = encodeURIComponent(SCOPES);
            const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?` +
                `client_id=${CLIENT_ID}&` +
                `redirect_uri=${encodeURIComponent(redirectUri)}&` +
                `scope=${scope}&` +
                `response_type=token&` +
                `prompt=consent&` +
                `state=${Date.now()}`;
            
            // Open popup window
            const popup = window.open(
                authUrl,
                'googleAuth',
                'width=500,height=600,scrollbars=yes,resizable=yes'
            );
            
            if (!popup) {
                throw new Error('Popup blocked. Please allow popups for this site.');
            }
            
            let resolved = false;
            
            // Cleanup function
            const cleanup = () => {
                window.removeEventListener('message', messageHandler);
                window.removeEventListener('storage', storageHandler);
                clearInterval(pollTimer);
            };
            
            // Listen for localStorage change (works even with COOP)
            const storageHandler = async (event: StorageEvent) => {
                if (event.key !== 'oauth_callback_data' || resolved) return;
                
                const data = event.newValue;
                if (!data) return;
                
                resolved = true;
                cleanup();
                localStorage.removeItem('oauth_callback_data');
                
                console.log('📥 Received OAuth token via localStorage');
                try {
                    const { accessToken, expiresIn, tokenType } = JSON.parse(data);
                    if (accessToken) {
                        const urlParams = new URLSearchParams();
                        urlParams.set('access_token', accessToken);
                        urlParams.set('expires_in', expiresIn || '3600');
                        urlParams.set('token_type', tokenType || 'Bearer');
                        handlePopupToken(accessToken, urlParams).then(resolve).catch(reject);
                    } else {
                        reject(new Error('No access token in storage data'));
                    }
                } catch (e) {
                    reject(new Error('Failed to parse OAuth data'));
                }
            };
            window.addEventListener('storage', storageHandler);
            
            // Also check localStorage on interval (in case storage event missed)
            const checkLocalStorage = () => {
                if (resolved) return;
                const data = localStorage.getItem('oauth_callback_data');
                if (data) {
                    try {
                        const parsed = JSON.parse(data);
                        // Only use if recent (within last 30 seconds)
                        if (Date.now() - parsed.timestamp < 30000) {
                            resolved = true;
                            cleanup();
                            localStorage.removeItem('oauth_callback_data');
                            console.log('📥 Found OAuth token in localStorage (poll)');
                            const urlParams = new URLSearchParams();
                            urlParams.set('access_token', parsed.accessToken);
                            urlParams.set('expires_in', parsed.expiresIn || '3600');
                            urlParams.set('token_type', parsed.tokenType || 'Bearer');
                            handlePopupToken(parsed.accessToken, urlParams).then(resolve).catch(reject);
                        }
                    } catch (e) {}
                }
            };
            
            // Listen for postMessage from popup (backup method)
            const messageHandler = async (event: MessageEvent) => {
                if (event.origin !== window.location.origin) return;
                if (event.data?.type !== 'OAUTH_CALLBACK') return;
                if (resolved) return;
                
                resolved = true;
                cleanup();
                
                console.log('📥 Received OAuth token via postMessage');
                const { accessToken, expiresIn, tokenType } = event.data;
                
                if (accessToken) {
                    const urlParams = new URLSearchParams();
                    urlParams.set('access_token', accessToken);
                    urlParams.set('expires_in', expiresIn || '3600');
                    urlParams.set('token_type', tokenType || 'Bearer');
                    handlePopupToken(accessToken, urlParams).then(resolve).catch(reject);
                } else {
                    reject(new Error('No access token in message'));
                }
            };
            window.addEventListener('message', messageHandler);
            
            // Poll popup for completion (fallback) and check localStorage
            const pollTimer = setInterval(() => {
                try {
                    if (resolved) {
                        cleanup();
                        return;
                    }
                    
                    // Check localStorage each poll
                    checkLocalStorage();
                    if (resolved) return;
                    
                    if (popup.closed) {
                        // Popup closed - check localStorage one more time with a small delay
                        // The popup might have just closed after writing to localStorage
                        setTimeout(() => {
                            if (resolved) return;
                            checkLocalStorage();
                            // Give a bit more time for the storage to be written
                            setTimeout(() => {
                                if (!resolved) {
                                    cleanup();
                                    reject(new Error('OAuth cancelled by user'));
                                }
                            }, 500);
                        }, 300);
                        clearInterval(pollTimer);
                        return;
                    }
                    
                    // Check if popup has redirected back to our domain (fallback method)
                    const popupUrl = popup.location.href;
                    if (popupUrl.includes(window.location.origin) && !resolved) {
                        const fragment = popup.location.hash.substring(1);
                        const urlParams = new URLSearchParams(fragment);
                        const accessToken = urlParams.get('access_token');
                        const error = urlParams.get('error');
                        
                        resolved = true;
                        cleanup();
                        popup.close();
                        
                        if (error) {
                            reject(new Error(error));
                            return;
                        }
                        
                        if (accessToken) {
                            handlePopupToken(accessToken, urlParams).then(resolve).catch(reject);
                        } else {
                            reject(new Error('No access token received'));
                        }
                    }
                } catch (e) {
                    // Cross-origin error, popup hasn't redirected back yet
                }
            }, 500); // Check every 500ms instead of 1s for faster response
            
        } catch (error) {
            console.error("Error during sign in:", error);
            reject(error);
        }
    });
};

export const signOut = () => {
    try {
        const token = gapi.client?.getToken();
        if (token && token.access_token) {
            // Revoke the token on Google's end
            fetch(`https://oauth2.googleapis.com/revoke?token=${token.access_token}`, {
                method: 'POST'
            }).catch(err => console.warn('Token revocation failed:', err));
        }
        
        // Clear local token and state
        if (gapi.client) {
            gapi.client.setToken(null);
        }
        appFolderId = null;
        
        // Clear stored profile and token
        localStorage.removeItem('driveUserProfile');
        clearStoredToken();
        
        console.log('👋 Drive disconnected successfully');
    } catch (error) {
        console.error('Error during Drive sign out:', error);
        // Still clear local data even if remote revocation fails
        localStorage.removeItem('driveUserProfile');
        clearStoredToken();
        appFolderId = null;
    }
};

// Try to auto-reconnect using stored token (for Premium users)
export const tryAutoReconnect = async (): Promise<UserProfile | null> => {
    try {
        // Check if we have a valid stored token
        const storedToken = getStoredToken();
        if (!storedToken) {
            console.log('🔐 No valid stored token for auto-reconnect');
            return null;
        }

        // Check if we have a stored profile
        const savedProfile = localStorage.getItem('driveUserProfile');
        if (!savedProfile) {
            console.log('🔐 No stored profile for auto-reconnect');
            clearStoredToken();
            return null;
        }

        // Wait for GAPI to be ready
        if (!gapiInited || !gapi.client) {
            await waitForGapi();
        }

        // Set the stored token in GAPI client
        gapi.client.setToken({
            access_token: storedToken.access_token,
            token_type: storedToken.token_type,
        });

        // Verify the token is still valid by making a test API call
        try {
            await gapi.client.drive.about.get({ fields: 'user' });
            console.log('✅ Auto-reconnect successful - Drive token is valid');
            return JSON.parse(savedProfile) as UserProfile;
        } catch (apiError: any) {
            // Token is invalid or expired
            console.log('⏰ Stored token is no longer valid:', apiError?.result?.error?.message || apiError);
            gapi.client.setToken(null);
            clearStoredToken();
            return null;
        }
    } catch (error) {
        console.error('❌ Auto-reconnect failed:', error);
        clearStoredToken();
        return null;
    }
};

// Check if auto-reconnect is possible (has stored token and profile)
export const canAutoReconnect = (): boolean => {
    const hasToken = getStoredToken() !== null;
    const hasProfile = localStorage.getItem('driveUserProfile') !== null;
    return hasToken && hasProfile;
};

const getAppFolderId = async (): Promise<string | null> => {
    if (appFolderId) return appFolderId;
    
    try {
        const response = await gapi.client.drive.files.list({
            q: `mimeType='application/vnd.google-apps.folder' and name='${APP_FOLDER_NAME}' and trashed=false`,
            fields: 'files(id, name)',
        });

        if (response.result.files && response.result.files.length > 0) {
            appFolderId = response.result.files[0].id!;
            return appFolderId;
        } else {
            const createResponse = await gapi.client.drive.files.create({
                resource: {
                    name: APP_FOLDER_NAME,
                    mimeType: 'application/vnd.google-apps.folder',
                },
                fields: 'id',
            });
            appFolderId = createResponse.result.id!;
            return appFolderId;
        }
    } catch (error) {
        console.error("Error finding or creating app folder:", error);
        return null;
    }
};

interface DriveChartInfo {
    id: string;
    title: string;
    modifiedTime?: string;
}

export const listCharts = async (): Promise<DriveChartInfo[]> => {
    const folderId = await getAppFolderId();
    if (!folderId) return [];
    
    try {
        const response = await gapi.client.drive.files.list({
            q: `'${folderId}' in parents and mimeType='application/json' and trashed=false`,
            fields: 'files(id, name, modifiedTime)',
            orderBy: 'name',
        });
        
        return response.result.files?.map((file: any) => ({
            id: file.id!,
            title: file.name!.replace('.json', ''),
            modifiedTime: file.modifiedTime,
        })) || [];
    } catch (error) {
        console.error("Error listing charts from Drive:", error);
        return [];
    }
};

export const getChartContent = async (fileId: string): Promise<SavedChartState | null> => {
    try {
        const response = await gapi.client.drive.files.get({
            fileId: fileId,
            alt: 'media',
        });
        return JSON.parse(response.body) as SavedChartState;
    } catch (error) {
        console.error(`Error getting chart content for ID ${fileId}:`, error);
        return null;
    }
};

export const saveChartToDrive = async (chart: SavedChartState): Promise<void> => {
    try {
        // Check if we have a valid token
        const token = gapi.client.getToken();
        if (!token) {
            throw new Error("No authentication token available");
        }
        
        console.log("Starting save to Drive for:", chart.songTitle);
        
        const folderId = await getAppFolderId();
        if (!folderId) {
            throw new Error("Could not get app folder ID.");
        }
        
        console.log("Got app folder ID:", folderId);
        
        const fileName = `${chart.songTitle}.json`;
        const chartData = JSON.stringify(chart, null, 2);

        // Check if the file already exists to decide whether to create or update.
        console.log("Checking for existing file:", fileName);
        const existingFilesResponse = await gapi.client.drive.files.list({
            q: `'${folderId}' in parents and name='${fileName}' and trashed=false`,
            fields: 'files(id, name)',
        });

        if (existingFilesResponse.result.files && existingFilesResponse.result.files.length > 0) {
            // --- UPDATE EXISTING FILE ---
            const fileId = existingFilesResponse.result.files[0].id;
            console.log(`Updating existing file with ID: ${fileId}`);
            
            const updateResponse = await gapi.client.request({
                path: `/upload/drive/v3/files/${fileId}`,
                method: 'PATCH',
                params: { uploadType: 'media' },
                headers: {
                    'Content-Type': 'application/json'
                },
                body: chartData
            });
            
            console.log("Update response:", updateResponse);

        } else {
            // --- CREATE NEW FILE ---
            console.log("Creating new file:", fileName);
            
            const boundary = '-------314159265358979323846';
            const delimiter = "\r\n--" + boundary + "\r\n";
            const close_delim = "\r\n--" + boundary + "--";
            
            const metadata = {
                'name': fileName,
                'parents': [folderId],
                'mimeType': 'application/json'
            };
            
            const multipartRequestBody =
                delimiter +
                'Content-Type: application/json\r\n\r\n' +
                JSON.stringify(metadata) +
                delimiter +
                'Content-Type: application/json\r\n\r\n' +
                chartData +
                close_delim;

            const createResponse = await gapi.client.request({
                'path': '/upload/drive/v3/files',
                'method': 'POST',
                'params': {'uploadType': 'multipart'},
                'headers': {
                    'Content-Type': 'multipart/related; boundary="' + boundary + '"'
                },
                'body': multipartRequestBody
            });
            
            console.log("Create response:", createResponse);
        }
        
        console.log(`Successfully saved chart: ${fileName}`);
        
    } catch (error) {
        console.error(`Error saving chart "${chart.songTitle}" to Drive:`, error);
        throw error;
    }
};

export const deleteChartFromDrive = async (title: string): Promise<void> => {
    const folderId = await getAppFolderId();
    if (!folderId) return;

    const fileName = `${title}.json`;

    try {
        const response = await gapi.client.drive.files.list({
            q: `'${folderId}' in parents and name='${fileName}' and trashed=false`,
            fields: 'files(id)',
        });

        if (response.result.files && response.result.files.length > 0) {
            const fileId = response.result.files[0].id!;
            await gapi.client.drive.files.delete({ fileId });
        }
    } catch (error) {
        console.error(`Error deleting chart "${title}" from Drive:`, error);
    }
};

// Collection/SetList functions for Google Drive sync
const COLLECTIONS_FILENAME = 'collections.json';

export const saveCollectionsToDrive = async (collections: ChartSetList[]): Promise<void> => {
    try {
        // Check if we have a valid token
        const token = gapi.client.getToken();
        if (!token) {
            throw new Error("No authentication token available");
        }
        
        console.log("Starting save collections to Drive, count:", collections.length);
        
        const folderId = await getAppFolderId();
        if (!folderId) {
            throw new Error("Could not get app folder ID.");
        }
        
        const collectionsData = JSON.stringify(collections, null, 2);

        // Check if the collections file already exists
        const existingFilesResponse = await gapi.client.drive.files.list({
            q: `'${folderId}' in parents and name='${COLLECTIONS_FILENAME}' and trashed=false`,
            fields: 'files(id, name)',
        });

        if (existingFilesResponse.result.files && existingFilesResponse.result.files.length > 0) {
            // Update existing collections file
            const fileId = existingFilesResponse.result.files[0].id;
            console.log(`Updating existing collections file with ID: ${fileId}`);
            
            await gapi.client.request({
                path: `/upload/drive/v3/files/${fileId}`,
                method: 'PATCH',
                params: { uploadType: 'media' },
                headers: {
                    'Content-Type': 'application/json'
                },
                body: collectionsData
            });

        } else {
            // Create new collections file
            console.log("Creating new collections file");
            
            const boundary = '-------314159265358979323846';
            const delimiter = "\r\n--" + boundary + "\r\n";
            const close_delim = "\r\n--" + boundary + "--";
            
            const metadata = {
                'name': COLLECTIONS_FILENAME,
                'parents': [folderId],
                'mimeType': 'application/json'
            };
            
            const multipartRequestBody =
                delimiter +
                'Content-Type: application/json\r\n\r\n' +
                JSON.stringify(metadata) +
                delimiter +
                'Content-Type: application/json\r\n\r\n' +
                collectionsData +
                close_delim;

            await gapi.client.request({
                'path': '/upload/drive/v3/files',
                'method': 'POST',
                'params': {'uploadType': 'multipart'},
                'headers': {
                    'Content-Type': 'multipart/related; boundary="' + boundary + '"'
                },
                'body': multipartRequestBody
            });
        }
        
        console.log(`Successfully saved ${collections.length} collections to Drive`);
        
    } catch (error) {
        console.error("Error saving collections to Drive:", error);
        throw error;
    }
};

export const loadCollectionsFromDrive = async (): Promise<ChartSetList[]> => {
    try {
        const folderId = await getAppFolderId();
        if (!folderId) return [];
        
        // Find the collections file
        const response = await gapi.client.drive.files.list({
            q: `'${folderId}' in parents and name='${COLLECTIONS_FILENAME}' and trashed=false`,
            fields: 'files(id, name)',
        });

        if (!response.result.files || response.result.files.length === 0) {
            console.log("No collections file found in Drive");
            return [];
        }

        // Get the file content
        const fileId = response.result.files[0].id!;
        const contentResponse = await gapi.client.drive.files.get({
            fileId: fileId,
            alt: 'media',
        });

        const collections = JSON.parse(contentResponse.body) as ChartSetList[];
        console.log(`Loaded ${collections.length} collections from Drive`);
        return collections;
        
    } catch (error) {
        console.error("Error loading collections from Drive:", error);
        return [];
    }
};
